import React, { useState, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  TitleBar,
  useCurrentPage,
} from 'src/components/DNA';
import PromptValuesStateProvider, {
  usePromptValuesState,
  PROMPT_TYPE_CONFIG,
} from 'src/state/context/PromptValues/PromptValuesStateProvider';
import { useDispatch } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import PromptValuesList from 'src/components/PromptValues/PromptValuesList';
import PromptValueModal from 'src/components/PromptValues/PromptValueModal';
import PromptTypeSelector from 'src/components/PromptValues/PromptTypeSelector';
import { PromptType, PromptValues } from '@alucio/aws-beacon-amplify/src/models';

const styles = StyleSheet.create({
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    minHeight: 'calc(100vh - 160px)',
    overflowX: 'auto',
  },
});

interface PromptValueModalProps {
  closeModal: () => void;
  onSave: (text: string) => void;
  rule?: PromptValues | null;
  title?: string;
  promptType: PromptType;
}

const PromptValueModalWrapper: React.FC<PromptValueModalProps> = ({
  closeModal,
  onSave,
  rule = null,
  title,
  promptType,
}) => {
  const config = PROMPT_TYPE_CONFIG[promptType];

  return (
    <PromptValueModal
      closeModal={closeModal}
      onSave={onSave}
      value={rule}
      config={config}
      title={title}
    />
  );
};

interface PromptValuesListWrapperProps {
  onCreateRule: () => void;
}

const PromptValuesListWrapper: React.FC<PromptValuesListWrapperProps> = ({ onCreateRule }) => {
  return <PromptValuesList onCreate={onCreateRule} />;
};

interface PublisherComplianceContentProps {
  selectedPromptType: PromptType;
  onPromptTypeChange: (type: PromptType) => void;
}

const PublisherComplianceContent: React.FC<PublisherComplianceContentProps> = ({
  selectedPromptType,
  onPromptTypeChange,
}) => {
  const currentPage = useCurrentPage();
  const { handleCreate, config } = usePromptValuesState();
  const dispatch = useDispatch();

  const onAddRule = useCallback((): void => {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: (modalProps) => (
        <PromptValueModalWrapper
          {...modalProps}
          onSave={handleCreate}
          title={config.modalTitles.create}
          promptType={selectedPromptType}
        />
      ),
    }));
  }, [dispatch, handleCreate, config.modalTitles.create, selectedPromptType]);

  return (
    <DNABox style={styles.mainWrapper} appearance="col">
      {/* HEADER */}
      <DNABox>
        <TitleBar
          title={currentPage?.LABEL || 'Compliance'}
          description={selectedPromptType === PromptType.AI_SUMMARY_DOC_TYPE
            ? 'Add prioritized document types for AI search summaries'
            : selectedPromptType === PromptType.AI_SUMMARY_INSTRUCTIONS
              ? 'Add instructions to guide AI search summaries'
              : selectedPromptType === PromptType.AI_SEARCH_ABBREVIATIONS
                ? 'Add abbreviations to improve AI search accuracy'
                : `Add ${config.pluralName.toLowerCase()} for custom decks`}
        >
          <TitleBar.Button>
            <DNAButton
              testID="add-rule-button"
              onPress={onAddRule}
              appearance="filled"
              status="primary"
            >
              {config.emptyState.actionLabel}
            </DNAButton>
          </TitleBar.Button>
        </TitleBar>
      </DNABox>

      {/* CONTENT */}
      <DNABox appearance="col" spacing="lg">
        <PromptTypeSelector
          selectedType={selectedPromptType}
          onTypeChange={onPromptTypeChange}
        />
        <PromptValuesListWrapper onCreateRule={onAddRule} />
      </DNABox>
    </DNABox>
  );
};

const PublisherCompliance = () => {
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType>(PromptType.DECK_COMPLIANCE_RULE);

  const handlePromptTypeChange = useCallback((newType: PromptType) => {
    setSelectedPromptType(newType);
  }, []);

  return (
    <PromptValuesStateProvider promptType={selectedPromptType}>
      <PublisherComplianceContent
        selectedPromptType={selectedPromptType}
        onPromptTypeChange={handlePromptTypeChange}
      />
    </PromptValuesStateProvider>
  );
};

PublisherCompliance.displayName = 'PublisherCompliance';

export default PublisherCompliance;
